import React, { useState, useEffect } from 'react'
import {
  Settings,
  Shield,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Edit2,
  X,
  Link as LinkIcon,
  Upload,
  FileText,
  ExternalLink
} from 'lucide-react'

// Helper to create fetch options with credentials for auth cookie
const createFetchOptions = (options: RequestInit = {}): RequestInit => {
  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...(options.headers as Record<string, string> || {})
  }

  // Add Authorization header if token exists in localStorage
  const token = localStorage.getItem('auth-token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return {
    ...options,
    credentials: 'include',
    headers
  }
}

interface SecurityRequirement {
  id: string
  label: string
  required: boolean
  referenceUrl?: string // Optional URL to attestation document
  referenceDocument?: {
    filename: string
    uploadedAt: string
    blobKey: string // Key in Netlify Blobs for retrieval
  }
}

interface DataClassificationLevel {
  id: string
  label: string
  description: string
  color: 'red' | 'orange' | 'yellow' | 'green'
  requireSecurityReview: boolean
  requireManagerApproval: boolean
  requirements: SecurityRequirement[]
  requirementLogic?: 'AND' | 'OR' // How to combine requirements (default: AND - all must be met)
}

interface TPRMConfig {
  dataClassificationLevels: DataClassificationLevel[]
  availableRequirements: SecurityRequirement[]
}

const DEFAULT_CONFIG: TPRMConfig = {
  dataClassificationLevels: [
    {
      id: 'L0',
      label: 'L0 - Customer Data',
      description: 'Most sensitive data - requires security team review',
      color: 'red',
      requireSecurityReview: true,
      requireManagerApproval: false,
      requirements: [
        { id: 'hasPrivacyPolicy', label: 'Privacy Policy', required: true },
        { id: 'hasSoc2', label: 'SOC 2 Certification', required: true },
        { id: 'hasIso27001', label: 'ISO 27001 Certification', required: true },
        { id: 'hasPenetrationTesting', label: 'Penetration Testing', required: true },
        { id: 'hasDpa', label: 'Data Processing Agreement (DPA)', required: true }
      ]
    },
    {
      id: 'L1',
      label: 'L1 - PII',
      description: 'Personal Identifiable Information',
      color: 'orange',
      requireSecurityReview: false,
      requireManagerApproval: true,
      requirements: [
        { id: 'hasPrivacyPolicy', label: 'Privacy Policy', required: true },
        { id: 'hasSoc2', label: 'SOC 2 Certification', required: true },
        { id: 'hasDpa', label: 'Data Processing Agreement (DPA)', required: true }
      ]
    },
    {
      id: 'L2',
      label: 'L2 - Non-public',
      description: 'Internal use only, not for public distribution',
      color: 'yellow',
      requireSecurityReview: false,
      requireManagerApproval: true,
      requirements: [
        { id: 'hasPrivacyPolicy', label: 'Privacy Policy', required: true },
        { id: 'hasSoc2', label: 'SOC 2 Certification', required: true }
      ]
    },
    {
      id: 'L3',
      label: 'L3 - Public',
      description: 'Public data, lowest security requirements',
      color: 'green',
      requireSecurityReview: false,
      requireManagerApproval: false,
      requirements: [
        { id: 'hasPrivacyPolicy', label: 'Privacy Policy', required: true }
      ]
    }
  ],
  availableRequirements: [
    { id: 'hasPrivacyPolicy', label: 'Privacy Policy', required: false },
    { id: 'hasSoc2', label: 'SOC 2 Certification', required: false },
    { id: 'hasIso27001', label: 'ISO 27001 Certification', required: false },
    { id: 'hasPenetrationTesting', label: 'Penetration Testing', required: false },
    { id: 'hasDpa', label: 'Data Processing Agreement (DPA)', required: false },
    { id: 'hasGdprCompliance', label: 'GDPR Compliance', required: false },
    { id: 'hasCcpaCompliance', label: 'CCPA Compliance', required: false },
    { id: 'hasHipaaCompliance', label: 'HIPAA Compliance', required: false }
  ]
}

const TPRMSettings: React.FC = () => {
  const [config, setConfig] = useState<TPRMConfig>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editingRequirement, setEditingRequirement] = useState<SecurityRequirement | null>(null)
  const [newRequirementLabel, setNewRequirementLabel] = useState('')
  const [showAddRequirement, setShowAddRequirement] = useState(false)
  const [editingRequirementRef, setEditingRequirementRef] = useState<string | null>(null)
  const [tempReferenceUrl, setTempReferenceUrl] = useState('')
  const [uploadingReferenceDoc, setUploadingReferenceDoc] = useState<string | null>(null)

  // Contract Settings State
  const [contractSettings, setContractSettings] = useState<{ employeeCount?: number; employeeCountByYear?: Array<{ year: string; count: number }> } | null>(null)
  const [savingContractSettings, setSavingContractSettings] = useState(false)
  const [contractSettingsSaved, setContractSettingsSaved] = useState(false)

  useEffect(() => {
    loadConfig()
    loadContractSettings()
  }, [])

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/tprm-config', createFetchOptions())
      if (!response.ok) {
        const errorText = await response.text()
        console.error(`Error loading TPRM config: HTTP ${response.status} - ${errorText}`)
        setLoadError(`Failed to load configuration (HTTP ${response.status})`)
      } else {
        const data = await response.json()
        if (data.config) {
          setConfig(data.config)
        }
        setLoadError(null)
      }
    } catch (error) {
      console.error('Error loading TPRM config:', error)
      setLoadError(error instanceof Error ? error.message : 'Failed to load configuration')
    } finally {
      setLoading(false)
    }
  }

  const saveConfig = async () => {
    setSaving(true)
    setSaved(false)

    try {
      const response = await fetch('/api/tprm-config', createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config })
      }))

      if (response.ok) {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      } else {
        alert('Failed to save configuration')
      }
    } catch (error) {
      console.error('Error saving TPRM config:', error)
      alert('Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const resetToDefaults = () => {
    if (confirm('Are you sure you want to reset to default settings? This cannot be undone.')) {
      setConfig(DEFAULT_CONFIG)
    }
  }

  const loadContractSettings = async () => {
    try {
      const response = await fetch('/api/tprm/contracts/settings', createFetchOptions())
      if (response.ok) {
        const data = await response.json()
        // Extract just the employee count fields from the full settings object
        if (data.settings) {
          setContractSettings({
            employeeCount: data.settings.employeeCount,
            employeeCountByYear: data.settings.employeeCountByYear
          })
        }
      }
    } catch (error) {
      console.error('Error loading contract settings:', error)
    }
  }

  const saveContractSettings = async () => {
    setSavingContractSettings(true)
    setContractSettingsSaved(false)

    try {
      const response = await fetch('/api/tprm/contracts/settings', createFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeCount: contractSettings?.employeeCount,
          employeeCountByYear: contractSettings?.employeeCountByYear
        })
      }))

      if (response.ok) {
        setContractSettingsSaved(true)
        setTimeout(() => setContractSettingsSaved(false), 3000)
      } else {
        alert('Failed to save contract settings')
      }
    } catch (error) {
      console.error('Error saving contract settings:', error)
      alert('Failed to save contract settings')
    } finally {
      setSavingContractSettings(false)
    }
  }

  const updateLevel = (levelId: string, updates: Partial<DataClassificationLevel>) => {
    setConfig({
      ...config,
      dataClassificationLevels: config.dataClassificationLevels.map(level =>
        level.id === levelId ? { ...level, ...updates } : level
      )
    })
  }

  const toggleRequirement = (levelId: string, requirementId: string) => {
    setConfig({
      ...config,
      dataClassificationLevels: config.dataClassificationLevels.map(level => {
        if (level.id === levelId) {
          const hasRequirement = level.requirements.some(r => r.id === requirementId)

          if (hasRequirement) {
            // Remove requirement
            return {
              ...level,
              requirements: level.requirements.filter(r => r.id !== requirementId)
            }
          } else {
            // Add requirement
            const requirement = config.availableRequirements.find(r => r.id === requirementId)
            if (requirement) {
              return {
                ...level,
                requirements: [...level.requirements, { ...requirement, required: true }]
              }
            }
          }
        }
        return level
      })
    })
  }

  const addNewRequirement = () => {
    if (!newRequirementLabel.trim()) {
      alert('Please enter a requirement name')
      return
    }

    // Generate ID from label (camelCase)
    const id = 'has' + newRequirementLabel.replace(/[^a-zA-Z0-9]/g, '').replace(/^./, str => str.toUpperCase())

    // Check if ID already exists
    if (config.availableRequirements.some(r => r.id === id)) {
      alert('A requirement with this name already exists')
      return
    }

    const newRequirement: SecurityRequirement = {
      id,
      label: newRequirementLabel.trim(),
      required: false
    }

    setConfig({
      ...config,
      availableRequirements: [...config.availableRequirements, newRequirement]
    })

    setNewRequirementLabel('')
    setShowAddRequirement(false)
  }

  const updateRequirementLabel = (requirementId: string, newLabel: string) => {
    setConfig({
      ...config,
      availableRequirements: config.availableRequirements.map(req =>
        req.id === requirementId ? { ...req, label: newLabel } : req
      ),
      // Update label in all levels that use this requirement
      dataClassificationLevels: config.dataClassificationLevels.map(level => ({
        ...level,
        requirements: level.requirements.map(req =>
          req.id === requirementId ? { ...req, label: newLabel } : req
        )
      }))
    })

    setEditingRequirement(null)
  }

  const deleteRequirement = (requirementId: string) => {
    // Check if requirement is in use
    const isInUse = config.dataClassificationLevels.some(level =>
      level.requirements.some(r => r.id === requirementId)
    )

    if (isInUse) {
      if (!confirm('This requirement is currently in use by one or more data classification levels. Are you sure you want to delete it?')) {
        return
      }
    }

    setConfig({
      ...config,
      availableRequirements: config.availableRequirements.filter(r => r.id !== requirementId),
      // Remove from all levels
      dataClassificationLevels: config.dataClassificationLevels.map(level => ({
        ...level,
        requirements: level.requirements.filter(r => r.id !== requirementId)
      }))
    })
  }

  const updateRequirementUrl = (requirementId: string, url: string) => {
    setConfig({
      ...config,
      availableRequirements: config.availableRequirements.map(req =>
        req.id === requirementId ? { ...req, referenceUrl: url || undefined } : req
      ),
      // Update in all levels that use this requirement
      dataClassificationLevels: config.dataClassificationLevels.map(level => ({
        ...level,
        requirements: level.requirements.map(req =>
          req.id === requirementId ? { ...req, referenceUrl: url || undefined } : req
        )
      }))
    })
    setEditingRequirementRef(null)
    setTempReferenceUrl('')
  }

  const handleReferenceDocumentUpload = async (requirementId: string, file: File) => {
    setUploadingReferenceDoc(requirementId)

    try {
      // Upload to reference documents endpoint
      const formData = new FormData()
      formData.append('file', file)
      formData.append('requirementId', requirementId)

      const response = await fetch('/api/tprm-reference-document-upload', createFetchOptions({
        method: 'POST',
        body: formData
      }))

      if (!response.ok) {
        throw new Error('Failed to upload document')
      }

      const result = await response.json()

      // Update config with document reference
      const docRef = {
        filename: file.name,
        uploadedAt: new Date().toISOString(),
        blobKey: result.blobKey
      }

      setConfig(prev => ({
        ...prev,
        availableRequirements: prev.availableRequirements.map(req =>
          req.id === requirementId ? { ...req, referenceDocument: docRef } : req
        ),
        // Update in all levels that use this requirement
        dataClassificationLevels: prev.dataClassificationLevels.map(level => ({
          ...level,
          requirements: level.requirements.map(req =>
            req.id === requirementId ? { ...req, referenceDocument: docRef } : req
          )
        }))
      }))
    } catch (error) {
      console.error('Error uploading reference document:', error)
      alert('Failed to upload document')
    } finally {
      setUploadingReferenceDoc(null)
    }
  }

  const removeReferenceDocument = (requirementId: string) => {
    if (!confirm('Remove this reference document?')) {
      return
    }

    setConfig({
      ...config,
      availableRequirements: config.availableRequirements.map(req =>
        req.id === requirementId ? { ...req, referenceDocument: undefined } : req
      ),
      // Update in all levels that use this requirement
      dataClassificationLevels: config.dataClassificationLevels.map(level => ({
        ...level,
        requirements: level.requirements.map(req =>
          req.id === requirementId ? { ...req, referenceDocument: undefined } : req
        )
      }))
    })
  }

  const toggleRequirementLogic = (levelId: string) => {
    setConfig({
      ...config,
      dataClassificationLevels: config.dataClassificationLevels.map(level =>
        level.id === levelId
          ? { ...level, requirementLogic: level.requirementLogic === 'OR' ? 'AND' : 'OR' }
          : level
      )
    })
  }

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'red':
        return 'bg-red-50 border-red-200 text-red-900'
      case 'orange':
        return 'bg-orange-50 border-orange-200 text-orange-900'
      case 'yellow':
        return 'bg-yellow-50 border-yellow-200 text-yellow-900'
      case 'green':
        return 'bg-green-50 border-green-200 text-green-900'
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Settings className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading TPRM settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Load Error Banner */}
      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Configuration Load Error</h3>
              <p className="mt-1 text-sm text-red-700">{loadError}</p>
              <button
                onClick={loadConfig}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">TPRM Configuration</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure data classification levels and approval workflows
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={resetToDefaults}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Reset to Defaults
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Settings className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900">How Approval Workflows Work</h3>
            <p className="text-sm text-blue-800 mt-1">
              Configure each data classification level's security requirements and approval workflow.
              When a vendor is assessed, the system will check if they meet all required controls
              and route accordingly.
            </p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
              <li><strong>Security Review:</strong> Always requires security team approval (typically L0)</li>
              <li><strong>Manager Approval:</strong> Requires manager approval if requirements aren't met</li>
              <li><strong>Auto-Approved:</strong> Automatically approved if all requirements are met</li>
              <li><strong>AND/OR Logic:</strong> Choose whether vendors must meet ALL requirements (AND) or ANY requirement (OR)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Manage Available Requirements */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Available Security Requirements</h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage the security requirements that can be assigned to data classification levels
            </p>
          </div>
          <button
            onClick={() => setShowAddRequirement(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            Add Requirement
          </button>
        </div>

        {/* Add New Requirement Form */}
        {showAddRequirement && (
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirement Name
                </label>
                <input
                  type="text"
                  value={newRequirementLabel}
                  onChange={(e) => setNewRequirementLabel(e.target.value)}
                  placeholder="e.g., PCI DSS Compliance"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addNewRequirement()
                    } else if (e.key === 'Escape') {
                      setShowAddRequirement(false)
                      setNewRequirementLabel('')
                    }
                  }}
                />
              </div>
              <button
                onClick={addNewRequirement}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                <CheckCircle className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  setShowAddRequirement(false)
                  setNewRequirementLabel('')
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Requirements List */}
        <div className="space-y-3">
          {config.availableRequirements.map((req) => {
            const isInUse = config.dataClassificationLevels.some(level =>
              level.requirements.some(r => r.id === req.id)
            )

            return (
              <div
                key={req.id}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {editingRequirement?.id === req.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={editingRequirement.label}
                      onChange={(e) => setEditingRequirement({ ...editingRequirement, label: e.target.value })}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updateRequirementLabel(req.id, editingRequirement.label)
                        } else if (e.key === 'Escape') {
                          setEditingRequirement(null)
                        }
                      }}
                    />
                    <button
                      onClick={() => updateRequirementLabel(req.id, editingRequirement.label)}
                      className="text-green-600 hover:text-green-700"
                      title="Save"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setEditingRequirement(null)}
                      className="text-gray-600 hover:text-gray-700"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Header with label and action buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{req.label}</div>
                        {isInUse && (
                          <div className="text-xs text-gray-500 mt-0.5">In use</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingRequirement(req)}
                          className="p-1 text-blue-600 hover:text-blue-700 rounded"
                          title="Edit name"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteRequirement(req.id)}
                          className="p-1 text-red-600 hover:text-red-700 rounded"
                          title="Delete requirement"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Reference URL Section */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-600">Reference URL (optional)</div>
                      {editingRequirementRef === req.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            value={tempReferenceUrl}
                            onChange={(e) => setTempReferenceUrl(e.target.value)}
                            placeholder="https://example.com/soc2-report"
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateRequirementUrl(req.id, tempReferenceUrl)
                              } else if (e.key === 'Escape') {
                                setEditingRequirementRef(null)
                                setTempReferenceUrl('')
                              }
                            }}
                          />
                          <button
                            onClick={() => updateRequirementUrl(req.id, tempReferenceUrl)}
                            className="p-1.5 text-green-600 hover:text-green-700"
                            title="Save URL"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingRequirementRef(null)
                              setTempReferenceUrl('')
                            }}
                            className="p-1.5 text-gray-600 hover:text-gray-700"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : req.referenceUrl ? (
                        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <LinkIcon className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                          <a
                            href={req.referenceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-xs text-blue-700 hover:text-blue-800 truncate"
                          >
                            {req.referenceUrl}
                          </a>
                          <button
                            onClick={() => {
                              setEditingRequirementRef(req.id)
                              setTempReferenceUrl(req.referenceUrl || '')
                            }}
                            className="p-1 text-blue-600 hover:text-blue-700"
                            title="Edit URL"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => updateRequirementUrl(req.id, '')}
                            className="p-1 text-red-600 hover:text-red-700"
                            title="Remove URL"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                          <a
                            href={req.referenceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-blue-600 hover:text-blue-700"
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingRequirementRef(req.id)
                            setTempReferenceUrl('')
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-white"
                        >
                          <LinkIcon className="h-3.5 w-3.5" />
                          Add Reference URL
                        </button>
                      )}
                    </div>

                    {/* Reference Document Section */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-600">Reference Document (optional)</div>
                      {req.referenceDocument ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                          <FileText className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-green-900 font-medium truncate">
                              {req.referenceDocument.filename}
                            </div>
                            <div className="text-xs text-green-700">
                              Uploaded {new Date(req.referenceDocument.uploadedAt).toLocaleDateString()}
                            </div>
                          </div>
                          <a
                            href={`/api/tprm-reference-document-download?blobKey=${req.referenceDocument.blobKey}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-green-600 hover:text-green-700"
                            title="Download document"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                          <button
                            onClick={() => removeReferenceDocument(req.id)}
                            className="p-1 text-red-600 hover:text-red-700"
                            title="Remove document"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            id={`ref-doc-${req.id}`}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  alert('File size must be less than 10MB')
                                  e.target.value = ''
                                  return
                                }
                                handleReferenceDocumentUpload(req.id, file)
                              }
                            }}
                            className="hidden"
                          />
                          <label
                            htmlFor={`ref-doc-${req.id}`}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs border border-gray-300 rounded cursor-pointer ${
                              uploadingReferenceDoc === req.id
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'text-gray-600 hover:bg-white'
                            }`}
                          >
                            {uploadingReferenceDoc === req.id ? (
                              <>
                                <Settings className="h-3.5 w-3.5 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-3.5 w-3.5" />
                                Upload Reference Document
                              </>
                            )}
                          </label>
                          <span className="text-xs text-gray-500">PDF, Word, or Image (max 10MB)</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Data Classification Levels */}
      <div className="space-y-4">
        {config.dataClassificationLevels.map((level) => (
          <div key={level.id} className={`border rounded-lg p-6 ${getColorClasses(level.color)}`}>
            {/* Level Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{level.label}</h3>
                <Shield className="h-5 w-5" />
              </div>
              <p className="text-sm opacity-80">{level.description}</p>
            </div>

            {/* Approval Workflow */}
            <div className="mb-4 pb-4 border-b border-current border-opacity-20">
              <h4 className="text-sm font-semibold mb-3">Approval Workflow</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={level.requireSecurityReview}
                    onChange={(e) => updateLevel(level.id, {
                      requireSecurityReview: e.target.checked,
                      requireManagerApproval: e.target.checked ? false : level.requireManagerApproval
                    })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">
                    Require Security Team Review (if vendor doesn't meet all security requirements)
                  </span>
                </label>

                {!level.requireSecurityReview && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={level.requireManagerApproval}
                      onChange={(e) => updateLevel(level.id, { requireManagerApproval: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">
                      Require Manager Approval (if vendor doesn't meet all requirements)
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Security Requirements */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold">Required Security Controls</h4>
                {level.requirements.length > 1 && (
                  <button
                    onClick={() => toggleRequirementLogic(level.id)}
                    className="flex items-center gap-2 px-3 py-1 bg-white bg-opacity-50 hover:bg-opacity-70 rounded-md text-xs font-medium transition-colors"
                    title="Toggle requirement logic: AND requires all controls, OR requires any control"
                  >
                    Logic: <span className="font-bold">{level.requirementLogic || 'AND'}</span>
                  </button>
                )}
              </div>

              {level.requirements.length > 0 && (
                <div className="mb-3 p-2 bg-white bg-opacity-30 rounded text-xs">
                  <span className="font-medium">
                    {level.requirementLogic === 'OR'
                      ? 'Vendor must meet ANY ONE of the selected controls'
                      : 'Vendor must meet ALL of the selected controls'}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {config.availableRequirements.map((req) => {
                  const isSelected = level.requirements.some(r => r.id === req.id)
                  return (
                    <label
                      key={req.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-white bg-opacity-50'
                          : 'hover:bg-white hover:bg-opacity-20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRequirement(level.id, req.id)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">{req.label}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Requirements Summary */}
            <div className="mt-4 pt-4 border-t border-current border-opacity-20">
              <p className="text-xs opacity-80">
                <strong>Approval Logic:</strong>{' '}
                {level.requireSecurityReview
                  ? `Vendors with this classification require security team review if missing any of ${level.requirements.length} required control(s). Auto-approved if all controls present.`
                  : level.requireManagerApproval
                  ? `Vendors missing any of ${level.requirements.length} required control(s) need manager approval. Auto-approved if all controls present.`
                  : `Auto-approved if vendor has all ${level.requirements.length} required control(s)`}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Contract Management Settings Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Contract Management Settings</h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure settings for contract analytics and dashboards
            </p>
          </div>
          <button
            onClick={saveContractSettings}
            disabled={savingContractSettings}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {savingContractSettings ? (
              <>
                <Settings className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : contractSettingsSaved ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Settings
              </>
            )}
          </button>
        </div>

        <div className="space-y-4">
          {/* Year-over-Year Employee Counts */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Employee Count by Year
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Track employee count changes over time for accurate year-over-year spend per employee calculations
            </p>

            {/* Existing Year Entries */}
            {contractSettings?.employeeCountByYear && contractSettings.employeeCountByYear.length > 0 ? (
              <div className="space-y-2 mb-3">
                {contractSettings.employeeCountByYear
                  .map((entry, originalIndex) => ({ entry, originalIndex }))
                  .sort((a, b) => b.entry.year.localeCompare(a.entry.year))
                  .map(({ entry, originalIndex }, displayIndex) => (
                  <div key={`year-entry-${displayIndex}`} className="flex items-center gap-3 bg-gray-50 p-3 rounded-md">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-gray-500">Year</label>
                        <input
                          type="text"
                          value={entry.year}
                          onChange={(e) => {
                            const updated = [...(contractSettings.employeeCountByYear || [])]
                            updated[originalIndex] = {
                              ...entry,
                              year: e.target.value
                            }
                            setContractSettings({
                              ...contractSettings,
                              employeeCountByYear: updated
                            })
                          }}
                          placeholder="e.g., 2024"
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Employee Count</label>
                        <input
                          type="text"
                          value={entry.count}
                          onChange={(e) => {
                            const updated = [...(contractSettings.employeeCountByYear || [])]
                            const newValue = parseInt(e.target.value)
                            updated[originalIndex] = {
                              ...entry,
                              count: isNaN(newValue) ? entry.count : (newValue || 1)
                            }
                            setContractSettings({
                              ...contractSettings,
                              employeeCountByYear: updated
                            })
                          }}
                          placeholder="e.g., 250"
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const updated = contractSettings.employeeCountByYear!.filter((_, idx) => idx !== originalIndex)
                        setContractSettings({
                          ...contractSettings,
                          employeeCountByYear: updated.length > 0 ? updated : undefined
                        })
                      }}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                      title="Remove year"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic mb-3">No year-specific employee counts configured yet</p>
            )}

            {/* Add New Year Button */}
            <button
              onClick={() => {
                const currentYear = new Date().getFullYear().toString()
                const existingYears = (contractSettings?.employeeCountByYear || []).map(e => e.year)
                setContractSettings({
                  ...contractSettings,
                  employeeCountByYear: [
                    ...(contractSettings?.employeeCountByYear || []),
                    { year: currentYear, count: contractSettings?.employeeCount || 1 }
                  ]
                })
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Year
            </button>
          </div>

          {/* Legacy Single Employee Count (backward compatibility) */}
          {!contractSettings?.employeeCountByYear || contractSettings.employeeCountByYear.length === 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Employee Count
              </label>
              <input
                type="text"
                value={contractSettings?.employeeCount || 1}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value)
                  setContractSettings({
                    ...contractSettings,
                    employeeCount: isNaN(newValue) ? (contractSettings?.employeeCount || 1) : (newValue || 1)
                  })
                }}
                className="w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., 250"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used for all years when year-specific counts are not configured
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default TPRMSettings
